import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  Lightbulb, ListChecks, Rocket, Clock, Check, X, Lock, AlertCircle, ListOrdered, MessageCircle, Trophy, RefreshCw, Target, Sparkles,
  LifeBuoy, Zap,
} from "lucide-react";
import Layout from "../../components/Layout/Layout";
import api from "../../services/api";
import StatusIcon from "./StatusIcon";
import { formatNaira } from "../../components/Billing/UpgradeBanner";
import Diagram from "./Diagram";
import { errMsg, classQuery, readingMinutes, LETTERS } from "./courseUtils";
import "./courses.css";

const paragraphs = (text) => String(text || "").split("\n").map((p) => p.trim()).filter(Boolean);

const CourseLesson = () => {
  const { slug, chapterId } = useParams();
  const [params] = useSearchParams();
  const cls = params.get("class") || "";
  const reviewMode = params.get("review") === "1";     // a quick review: straight to a fresh quiz on something already learned
  const navigate = useNavigate();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null); // {status, message}
  const [phase, setPhase] = useState("lesson"); // lesson | quiz | results
  const [answers, setAnswers] = useState({});
  const [qIndex, setQIndex] = useState(0);
  const [partIdx, setPartIdx] = useState(0);
  const [setId, setSetId] = useState(null); // a fresh retake set from the server
  const [retrying, setRetrying] = useState(false);
  const [retryNote, setRetryNote] = useState("");
  const [explained, setExplained] = useState({}); // "part:mode" -> {loading, error, content}
  const [explainOpen, setExplainOpen] = useState(null);
  const [practice, setPractice] = useState(null); // {loading, error, data, picked}
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [result, setResult] = useState(null);
  const [drawer, setDrawer] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const nextPhase = useRef("lesson");
  const reviewStarted = useRef(false);
  const [supportHidden, setSupportHidden] = useState(false);

  const q = classQuery(data?.class || cls);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    setData((d) => (d && d.chapter?.id === chapterId ? d : null));
    api
      .get(`/api/courses/${slug}/chapters/${chapterId}`, { params: cls ? { class: cls } : {}, timeout: 180000 })
      .then((res) => {
        if (cancelled) return;
        setData(res.data);
        setAnswers({});
        setQIndex(0);
        setPartIdx(0);
        setSetId(null);
        setExplained({});
        setExplainOpen(null);
        setPractice(null);
        setResult(null);
        setSubmitError("");
        setPhase(nextPhase.current);
        nextPhase.current = "lesson";
      })
      .catch((e) => {
        if (cancelled) return;
        nextPhase.current = "lesson";
        const detail = e?.response?.data?.detail;
        setError({ status: e?.response?.status, message: errMsg(e, ""), paywall: e?.response?.status === 402 && detail && typeof detail === "object" ? detail : null });
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [slug, chapterId, cls, reloadKey]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [phase, chapterId, partIdx]);

  useEffect(() => {
    setDrawer(false);
  }, [chapterId]);

  const questions = data?.quiz?.questions || [];
  const passMark = data?.quiz?.pass_mark ?? 60;
  const missing = questions.map((qu, i) => (answers[qu.id] ? null : i + 1)).filter(Boolean);

  const submit = async () => {
    setSubmitting(true);
    setSubmitError("");
    try {
      const res = await api.post(`/api/courses/${slug}/chapters/${chapterId}/quiz`, {
        answers,
        class: data?.class || cls || undefined,
        set_id: setId || undefined,
      });
      setResult(res.data);
      setPhase("results");
    } catch (e) {
      setSubmitError(errMsg(e, "We couldn't check your answers. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const retryQuiz = async () => {
    setRetrying(true);
    setRetryNote("");
    try {
      const res = await api.post(
        `/api/courses/${slug}/chapters/${chapterId}/quiz/new`,
        { class: data?.class || cls || undefined },
        { timeout: 120000 }
      );
      setData((d) => ({ ...d, quiz: res.data.quiz }));
      setSetId(res.data.set_id);
      setAnswers({});
      setQIndex(0);
      setResult(null);
      setPractice(null);
      setPhase("quiz");
    } catch (e) {
      // fall back to the same questions, reshuffled
      setRetryNote(errMsg(e, "We couldn't write new questions, so here is the same quiz again."));
      nextPhase.current = "quiz";
      setReloadKey((k) => k + 1);
    } finally {
      setRetrying(false);
    }
  };

  // A quick review starts the quiz straight away, with new questions
  useEffect(() => {
    if (reviewMode && data && data.status === "passed" && phase === "lesson" && !reviewStarted.current) {
      reviewStarted.current = true;
      retryQuiz();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reviewMode, data, phase]);

  const askExplain = async (mode, part = partIdx, forceOpen = false) => {
    const key = `${part}:${mode}`;
    if (explainOpen === key && !forceOpen) {
      setExplainOpen(null);
      return;
    }
    setExplainOpen(key);
    if (explained[key]?.content) return;
    setExplained((x) => ({ ...x, [key]: { loading: true } }));
    try {
      const res = await api.post(
        `/api/courses/${slug}/chapters/${chapterId}/explain`,
        { class: data?.class || cls || undefined, part, mode },
        { timeout: 120000 }
      );
      setExplained((x) => ({ ...x, [key]: { content: res.data } }));
    } catch (e) {
      setExplained((x) => ({ ...x, [key]: { error: errMsg(e, "We couldn't explain it again right now.") } }));
    }
  };

  // "Let's slow down": jump to the part they missed and show it again in simpler words
  const reteach = (part) => {
    setPhase("lesson");
    setPartIdx(part);
    askExplain("simpler", part, true);
  };

  const loadPractice = async () => {
    setPractice({ loading: true, picked: {} });
    try {
      const res = await api.post(
        `/api/courses/${slug}/chapters/${chapterId}/practice`,
        { class: data?.class || cls || undefined },
        { timeout: 120000 }
      );
      setPractice({ data: res.data, picked: {} });
    } catch (e) {
      setPractice({ error: errMsg(e, "We couldn't prepare practice questions right now."), picked: {} });
    }
  };

  const goChapter = (id) => navigate(`/courses/${slug}/${id}${q}`);
  const toCourse = `/courses/${slug}${q}`;

  // ---------- States ----------
  if (loading && !data) {
    return (
      <Layout fullBleed>
        <div className="courses-page">
          <div className="cs-shell cs-narrow" aria-busy="true">
            <p className="cs-loading-text" role="status">
              Preparing your lesson… the first time can take a few seconds
            </p>
            <div className="cs-skel cs-skel-header" />
            <div className="cs-card cs-skel-card">
              <div className="cs-skel cs-skel-line" />
              <div className="cs-skel cs-skel-line" />
              <div className="cs-skel cs-skel-line short" />
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error?.paywall) {
    const from = encodeURIComponent(window.location.pathname + window.location.search);
    return (
      <Layout fullBleed>
        <div className="courses-page">
          <div className="cs-shell cs-narrow">
            <div className="cs-card cs-state">
              <span className="cs-state-icon"><Lock size={28} /></span>
              <h1 className="cs-state-title">
                This topic is for subscribers
              </h1>
              <p className="cs-muted">{error.paywall.message}</p>
              <div className="cs-actions center">
                <Link to={`/subscribe?from=${from}`} className="eg-btn eg-btn-accent">
                  Subscribe for {formatNaira(error.paywall.price_ngn)} a month
                </Link>
                <Link to={toCourse} className="eg-btn eg-btn-outline">Back to the course</Link>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    const locked = error.status === 403;
    return (
      <Layout fullBleed>
        <div className="courses-page">
          <div className="cs-shell cs-narrow">
            <div className="cs-card cs-state">
              <span className="cs-state-icon">{locked ? <Lock size={28} /> : <AlertCircle size={28} />}</span>
              <h1 className="cs-state-title">
                {locked
                  ? "This chapter is locked — pass the previous chapter's quiz first"
                  : error.status === 404
                  ? "We couldn't find this chapter"
                  : "We couldn't prepare this lesson right now"}
              </h1>
              {error.message && <p className="cs-muted">{error.message}</p>}
              <div className="cs-actions center">
                {!locked && error.status !== 404 && (
                  <button type="button" className="eg-btn eg-btn-accent" onClick={() => setReloadKey((k) => k + 1)}>
                    Retry
                  </button>
                )}
                <Link to={toCourse} className="eg-btn eg-btn-outline">Go to the course</Link>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!data) return null;

  const { chapter, topic, lesson = {}, sidebar = [], prev, next } = data;
  const minutes = readingMinutes(lesson);

  const sidebarList = (
    <nav aria-label="Chapters in this topic">
      <p className="cs-side-topic">{topic?.title}</p>
      <ol className="cs-side-list">
        {sidebar.map((c) => {
          const locked = c.status === "locked";
          const cur = c.current || c.id === chapter?.id;
          const inner = (
            <>
              <span className="cs-ch-num">{c.number}</span>
              <span className="cs-ch-title">{c.title}</span>
              <span className={`cs-status cs-status-${c.status}`}><StatusIcon status={c.status} /></span>
            </>
          );
          return (
            <li key={c.id}>
              {locked ? (
                <div className="cs-side-item locked" title="Pass the previous chapter's quiz to unlock" aria-disabled="true">
                  {inner}
                </div>
              ) : (
                <Link
                  to={`/courses/${slug}/${c.id}${q}`}
                  className={`cs-side-item ${cur ? "current" : ""}`}
                  aria-current={cur ? "page" : undefined}
                >
                  {inner}
                </Link>
              )}
            </li>
          );
        })}
      </ol>
      <Link to={toCourse} className="cs-side-back">← Back to all topics</Link>
    </nav>
  );

  const lessonParts = lesson.parts?.length
    ? lesson.parts
    : [{ title: "", sections: lesson.sections || [], key_points: lesson.key_points, try_it: lesson.try_it }];
  const lastPart = partIdx >= lessonParts.length - 1;

  const renderLesson = () => {
    const part = lessonParts[Math.min(partIdx, lessonParts.length - 1)];
    return (
      <article className="cs-lesson">
        <div className="cs-lesson-head">
          <span className="cs-eyebrow">{chapter?.position || `Chapter ${chapter?.number}`}</span>
          <h1 className="cs-lesson-title">{chapter?.title}</h1>
          <span className="cs-read-time"><Clock size={14} /> About {minutes} min read · {lessonParts.length} parts</span>
        </div>

        {data.support?.kind === "slow_down" && !supportHidden && (
          <aside className="cs-support slow" role="status">
            <span className="cs-support-icon"><LifeBuoy size={20} aria-hidden="true" /></span>
            <div>
              <strong>Let's take this at your pace</strong>
              <p>{data.support.message}</p>
              <div className="cs-actions wrap">
                <button type="button" className="eg-btn eg-btn-accent eg-btn-sm" onClick={() => reteach(data.support.part)}>
                  Explain {data.support.part_title ? `“${data.support.part_title}”` : "it"} more simply
                </button>
                <button type="button" className="cs-link" onClick={() => setSupportHidden(true)}>I'll read it myself</button>
              </div>
            </div>
          </aside>
        )}
        {data.support?.kind === "fast_track" && !supportHidden && (
          <aside className="cs-support fast" role="status">
            <span className="cs-support-icon"><Zap size={20} aria-hidden="true" /></span>
            <div>
              <strong>You're on a roll</strong>
              <p>{data.support.message}</p>
              <div className="cs-actions wrap">
                <button type="button" className="eg-btn eg-btn-accent eg-btn-sm" onClick={() => { setQIndex(0); setPhase("quiz"); }}>
                  Go straight to the quiz
                </button>
                <button type="button" className="cs-link" onClick={() => setSupportHidden(true)}>I'll read it first</button>
              </div>
            </div>
          </aside>
        )}

        {lessonParts.length > 1 && (
          <div className="cs-partbar" aria-label="Lesson parts">
            <span className="cs-muted-sm" aria-live="polite">Part {partIdx + 1} of {lessonParts.length}</span>
            <div className="cs-dots">
              {lessonParts.map((p, i) => (
                <button
                  key={i}
                  type="button"
                  className={`cs-dot ${i === partIdx ? "current" : ""} ${i < partIdx ? "answered" : ""}`}
                  aria-label={`Go to part ${i + 1}: ${p.title}`}
                  aria-current={i === partIdx ? "step" : undefined}
                  onClick={() => setPartIdx(i)}
                />
              ))}
            </div>
          </div>
        )}

        {partIdx === 0 && lesson.intro && <p className="cs-intro">{lesson.intro}</p>}
        {part.title && lessonParts.length > 1 && <h2 className="cs-part-title">{part.title}</h2>}

        {part.diagram?.shapes?.length > 0 && <Diagram diagram={part.diagram} />}

        {(part.sections || []).map((s, i) => (
          <section key={i} className="cs-section">
            {s.heading && <h3 className="cs-h2">{s.heading}</h3>}
            {paragraphs(s.body).map((p, j) => <p key={j} className="cs-p">{p}</p>)}
            {s.example && (
              <aside className="cs-example">
                <span className="cs-box-label"><Lightbulb size={16} /> Example{s.example.title && !/^examples?:?$/i.test(s.example.title.trim()) ? `: ${s.example.title.replace(/^examples?:\s*/i, "")}` : ""}</span>
                {paragraphs(s.example.body).map((p, j) => <p key={j} className="cs-p">{p}</p>)}
              </aside>
            )}
          </section>
        ))}

        <div className="cs-explain">
          <span className="cs-muted-sm">Finding this part hard?</span>
          <button type="button" className={`cs-chip ${explainOpen === `${partIdx}:simpler` ? "on" : ""}`} onClick={() => askExplain("simpler")}>
            <Sparkles size={14} aria-hidden="true" /> Explain it more simply
          </button>
          <button type="button" className={`cs-chip ${explainOpen === `${partIdx}:examples` ? "on" : ""}`} onClick={() => askExplain("examples")}>
            <Lightbulb size={14} aria-hidden="true" /> Show me more examples
          </button>
        </div>
        {explainOpen && explainOpen.startsWith(`${partIdx}:`) && (() => {
          const ex = explained[explainOpen] || {};
          return (
            <aside className="cs-explained" aria-live="polite">
              {ex.loading && <p className="cs-muted">Writing a new explanation…</p>}
              {ex.error && <p className="cs-alert-text">{ex.error}</p>}
              {ex.content && (
                <>
                  <span className="cs-box-label">
                    <Sparkles size={16} /> {ex.content.mode === "examples" ? "More examples" : "In simpler words"}
                  </span>
                  {(ex.content.sections || []).map((sec, i) => (
                    <div key={i} className="cs-section">
                      {sec.heading && <h4 className="cs-h3">{sec.heading}</h4>}
                      {paragraphs(sec.body).map((p, j) => <p key={j} className="cs-p">{p}</p>)}
                      {sec.example && (
                        <div className="cs-example inner">
                          {paragraphs(sec.example.body).map((p, j) => <p key={j} className="cs-p">{p}</p>)}
                        </div>
                      )}
                    </div>
                  ))}
                  {ex.content.recap && <p className="cs-p"><strong>{ex.content.recap}</strong></p>}
                </>
              )}
              <button type="button" className="cs-link" onClick={() => setExplainOpen(null)}>Close</button>
            </aside>
          );
        })()}

        {part.key_points?.length > 0 && (
          <aside className="cs-keypoints">
            <span className="cs-box-label"><ListChecks size={16} /> Key points</span>
            <ul>
              {part.key_points.map((k, i) => (
                <li key={i}><Check size={16} aria-hidden="true" /> <span>{k}</span></li>
              ))}
            </ul>
          </aside>
        )}

        {part.try_it && (
          <aside className="cs-tryit">
            <span className="cs-box-label"><Rocket size={16} /> Try it</span>
            {paragraphs(part.try_it).map((p, j) => <p key={j} className="cs-p">{p}</p>)}
          </aside>
        )}

        <div className="cs-actions between">
          {partIdx > 0 ? (
            <button type="button" className="eg-btn eg-btn-outline" onClick={() => setPartIdx(partIdx - 1)}>← Previous part</button>
          ) : prev ? (
            <button type="button" className="eg-btn eg-btn-outline" onClick={() => goChapter(prev.id)}>← Previous chapter</button>
          ) : <span />}
          {lastPart ? (
            <button
              type="button"
              className="eg-btn eg-btn-accent"
              onClick={() => { setQIndex(0); setPhase("quiz"); }}
              disabled={!questions.length}
            >
              Take the chapter quiz →
            </button>
          ) : (
            <button type="button" className="eg-btn eg-btn-primary" onClick={() => setPartIdx(partIdx + 1)}>
              Next part →
            </button>
          )}
        </div>
      </article>
    );
  };

  const renderQuiz = () => {
    const cur = questions[qIndex];
    if (!cur) return null;
    const last = qIndex === questions.length - 1;
    const allDone = missing.length === 0;
    return (
      <div className="cs-quiz">
        <div className="cs-quiz-head">
          <span className="cs-eyebrow">{reviewMode ? "Quick review" : "Chapter quiz"} · {questions.length} questions · pass mark {passMark}%</span>
          <h1 className="cs-lesson-title">{chapter?.title}</h1>
        </div>
        <div className="cs-qprogress">
          <span className="cs-muted-sm" aria-live="polite">Question {qIndex + 1} of {questions.length}</span>
          <div className="cs-dots">
            {questions.map((qu, i) => (
              <button
                key={qu.id}
                type="button"
                className={`cs-dot ${i === qIndex ? "current" : ""} ${answers[qu.id] ? "answered" : ""}`}
                aria-label={`Go to question ${i + 1}${answers[qu.id] ? " (answered)" : " (not answered)"}`}
                aria-current={i === qIndex ? "step" : undefined}
                onClick={() => setQIndex(i)}
              />
            ))}
          </div>
        </div>

        <div className="cs-card cs-qcard">
          <h2 className="cs-question" id="cs-q-text">{cur.question}</h2>
          <div className="cs-options" role="radiogroup" aria-labelledby="cs-q-text">
            {(cur.options || []).map((opt, i) => {
              const selected = answers[cur.id] === opt;
              return (
                <button
                  key={`${cur.id}-${i}`}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`cs-option ${selected ? "selected" : ""}`}
                  onClick={() => setAnswers((a) => ({ ...a, [cur.id]: opt }))}
                >
                  <span className="cs-letter">{LETTERS[i]}</span>
                  <span className="cs-option-text">{opt}</span>
                  {selected && <Check size={20} className="cs-option-check" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </div>

        {last && !allDone && (
          <p className="cs-unanswered" role="status">
            Still to answer: question{missing.length > 1 ? "s" : ""} {missing.join(", ")}
          </p>
        )}
        {submitError && <div className="cs-alert" role="alert"><AlertCircle size={18} /><span>{submitError}</span></div>}

        <div className="cs-actions between">
          <button
            type="button"
            className="eg-btn eg-btn-outline"
            onClick={() => (qIndex === 0 ? (setPartIdx(lessonParts.length - 1), setPhase("lesson")) : setQIndex(qIndex - 1))}
          >
            {qIndex === 0 ? "← Back to lesson" : "Back"}
          </button>
          {last ? (
            <button type="button" className="eg-btn eg-btn-accent" onClick={submit} disabled={!allDone || submitting}>
              {submitting ? "Checking…" : "Submit answers"}
            </button>
          ) : (
            <button type="button" className="eg-btn eg-btn-primary" onClick={() => setQIndex(qIndex + 1)} disabled={!answers[cur.id]}>
              Next
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderResults = () => {
    if (!result) return null;
    const r = result;
    const nxt = r.next;
    return (
      <div className="cs-results">
        <div className="cs-card cs-score">
          <div className="cs-score-num">{r.score} / {r.total} · {r.percentage}%</div>
          <div className={`cs-banner ${r.passed ? "pass" : "fail"}`} role="status">
            {r.passed ? "You passed! 🎉" : "Almost there — review the lesson and try again"}
          </div>
          <p className="cs-muted">Pass mark: {r.pass_mark ?? passMark}%</p>
          {r.points_earned > 0 && <span className="cs-points">+{r.points_earned} points</span>}
        </div>

        {r.review && (
          <div className={`cs-support ${r.review.remembered ? "fast" : "slow"}`} role="status">
            <span className="cs-support-icon"><RefreshCw size={20} aria-hidden="true" /></span>
            <div>
              <strong>
                {r.review.finished ? "Locked in for good! 🎉"
                  : r.review.remembered ? "You still remember it!" : "Not quite there yet, and that is normal"}
              </strong>
              <p>
                {r.review.finished
                  ? "You've reviewed this enough times that it should stay with you. No more reminders for this one."
                  : r.review.remembered
                  ? `We'll check again in ${r.review.next_in_days} days, a bit later each time, so it stays fresh.`
                  : "Reading it again helps. We'll check again tomorrow, and the gaps get longer once it sticks."}
              </p>
            </div>
          </div>
        )}
        {r.support?.kind === "slow_down" && (
          <section className="cs-support slow big" role="status">
            <span className="cs-support-icon"><LifeBuoy size={22} aria-hidden="true" /></span>
            <div>
              <strong>This one is tricky, and that is completely fine</strong>
              <p>{r.support.message}</p>
              <ol className="cs-support-steps">
                <li>
                  <button type="button" className="eg-btn eg-btn-accent eg-btn-sm" onClick={() => reteach(r.support.part)}>
                    Read {r.support.part_title ? `“${r.support.part_title}”` : "the part you missed"} in simpler words
                  </button>
                </li>
                <li>
                  <button type="button" className="eg-btn eg-btn-primary eg-btn-sm" onClick={() => { if (!practice) loadPractice(); }}>
                    Practise what you missed
                  </button>
                </li>
                <li>
                  <button type="button" className="eg-btn eg-btn-outline eg-btn-sm" onClick={retryQuiz} disabled={retrying}>
                    {retrying ? "Writing new questions…" : "Try again with gentler questions"}
                  </button>
                </li>
              </ol>
            </div>
          </section>
        )}

        {r.passed && r.subject_complete && (
          <div className="cs-celebrate big">
            <Trophy size={36} aria-hidden="true" />
            <h2>You finished {data.subject}! 🏆</h2>
            <p>Amazing work. You have completed every chapter in this subject.</p>
          </div>
        )}
        {r.passed && r.topic_complete && !r.subject_complete && (
          <div className="cs-celebrate">
            <h2>Topic complete: {r.topic_title || topic?.title} 🎉</h2>
          </div>
        )}

        <div className="cs-actions wrap">
          {r.passed ? (
            <>
              {nxt && (
                <button type="button" className="eg-btn eg-btn-accent" onClick={() => goChapter(nxt.id)}>
                  {r.topic_complete ? "Next topic →" : "Next chapter →"}
                </button>
              )}
              {!nxt && (
                <Link to={toCourse} className="eg-btn eg-btn-accent">Back to course</Link>
              )}
            </>
          ) : (
            <>
              <button type="button" className="eg-btn eg-btn-primary" onClick={() => { setPhase("lesson"); }}>
                Review the lesson
              </button>
              <button type="button" className="eg-btn eg-btn-accent" onClick={retryQuiz} disabled={retrying}>
                {retrying ? "Writing new questions…" : "Try again with new questions"}
              </button>
            </>
          )}
          {r.passed && (
            <button type="button" className="eg-btn eg-btn-outline" onClick={retryQuiz} disabled={retrying}>
              <RefreshCw size={16} aria-hidden="true" /> {retrying ? "Writing new questions…" : "Retake with new questions"}
            </button>
          )}
          {(!r.passed || nxt) && <Link to={toCourse} className="eg-btn eg-btn-outline">Back to course</Link>}
        </div>
        {retryNote && <div className="cs-alert" role="status"><AlertCircle size={18} /><span>{retryNote}</span></div>}

        {(r.results || []).some((it) => !it.is_correct) && (
          <section className="cs-card cs-practice">
            <h2 className="cs-h2"><Target size={18} aria-hidden="true" /> Practise what you missed</h2>
            {!practice && (
              <>
                <p className="cs-muted">
                  Get a short recap of the {(r.results || []).filter((it) => !it.is_correct).length === 1 ? "idea" : "ideas"} you
                  missed and a few quick practice questions. They don't affect your score.
                </p>
                <button type="button" className="eg-btn eg-btn-primary" onClick={loadPractice}>Start practice</button>
              </>
            )}
            {practice?.loading && <p className="cs-muted" role="status">Preparing your practice…</p>}
            {practice?.error && (
              <div className="cs-alert" role="alert">
                <AlertCircle size={18} /><span>{practice.error}</span>
                <button type="button" className="cs-link" onClick={loadPractice}>Try again</button>
              </div>
            )}
            {practice?.data && (
              <>
                <div className="cs-recaps">
                  {practice.data.recaps.map((rc, i) => (
                    <div key={i} className="cs-recap">
                      <strong>{rc.concept}</strong>
                      <p className="cs-p">{rc.explanation}</p>
                    </div>
                  ))}
                </div>
                <ol className="cs-practice-list">
                  {practice.data.questions.map((qu) => {
                    const picked = practice.picked[qu.id];
                    return (
                      <li key={qu.id} className="cs-practice-q">
                        <p className="cs-question">{qu.question}</p>
                        <div className="cs-options">
                          {qu.options.map((opt, i) => {
                            const isRight = opt === qu.answer;
                            const state = picked ? (isRight ? "correct" : picked === opt ? "wrong" : "") : "";
                            return (
                              <button
                                key={i}
                                type="button"
                                disabled={!!picked}
                                className={`cs-option ${state}`}
                                onClick={() => setPractice((pr) => ({ ...pr, picked: { ...pr.picked, [qu.id]: opt } }))}
                              >
                                <span className="cs-letter">{LETTERS[i]}</span>
                                <span className="cs-option-text">{opt}</span>
                                {state === "correct" && <Check size={18} aria-hidden="true" />}
                                {state === "wrong" && <X size={18} aria-hidden="true" />}
                              </button>
                            );
                          })}
                        </div>
                        {picked && (
                          <p className={`cs-practice-fb ${picked === qu.answer ? "ok" : "bad"}`}>
                            {picked === qu.answer ? "Correct! " : `Not quite — the answer is ${qu.answer}. `}
                            {qu.explanation}
                          </p>
                        )}
                      </li>
                    );
                  })}
                </ol>
                <div className="cs-actions wrap">
                  <button type="button" className="eg-btn eg-btn-accent" onClick={retryQuiz} disabled={retrying}>
                    {retrying ? "Writing new questions…" : "Ready — retake the quiz with new questions"}
                  </button>
                  <button type="button" className="eg-btn eg-btn-outline" onClick={loadPractice}>More practice</button>
                </div>
              </>
            )}
          </section>
        )}

        <h2 className="cs-h2">Review your answers</h2>
        <ol className="cs-review">
          {(r.results || []).map((it, i) => (
            <li key={it.id} className={`cs-review-item ${it.is_correct ? "ok" : "bad"}`}>
              <div className="cs-review-q">
                <span className="cs-review-icon">{it.is_correct ? <Check size={16} /> : <X size={16} />}</span>
                <span><strong>{i + 1}.</strong> {it.question}</span>
              </div>
              <p className="cs-review-line">
                Your answer: <strong>{it.your_answer ?? "No answer"}</strong>
              </p>
              {!it.is_correct && (
                <p className="cs-review-line">Correct answer: <strong>{it.correct_answer}</strong></p>
              )}
              {it.explanation && <p className="cs-review-expl">{it.explanation}</p>}
            </li>
          ))}
        </ol>
      </div>
    );
  };

  return (
    <Layout fullBleed>
      <div className="courses-page">
        <div className="cs-shell cs-wide">
          <nav className="cs-crumbs" aria-label="Breadcrumb">
            <Link to={`/courses${q}`}>Courses</Link>
            <span aria-hidden="true">›</span>
            <Link to={toCourse}>{data.subject}</Link>
            <span aria-hidden="true">›</span>
            <span aria-current="page">{topic?.title}</span>
          </nav>

          <div className="cs-lesson-layout">
            <aside className="cs-sidebar">
              <button
                type="button"
                className="cs-drawer-btn"
                aria-expanded={drawer}
                onClick={() => setDrawer((d) => !d)}
              >
                <ListOrdered size={18} /> Chapters
              </button>
              <div className={`cs-side-panel ${drawer ? "open" : ""}`}>{sidebarList}</div>
            </aside>

            <div className="cs-main">
              {loading && <p className="cs-loading-text" role="status">Preparing your lesson…</p>}
              {phase === "lesson" && renderLesson()}
              {phase === "quiz" && renderQuiz()}
              {phase === "results" && renderResults()}

              <div className="cs-help">
                <MessageCircle size={16} aria-hidden="true" />
                <span>Stuck?</span>
                <button
                  type="button"
                  className="cs-link-btn"
                  onClick={() =>
                    navigate("/agents/tutor", { state: { context: { subject: data.subject, topic: chapter?.title } } })
                  }
                >
                  Ask the AI tutor
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CourseLesson;
